import type { ChatOutboxEntry, ChatOutboxKind } from "@/domains/offline/db/schema";

export type { ChatOutboxEntry };

const transientChatOutbox = new Map<string, ChatOutboxEntry>();

export async function enqueueChatOutbox(
  entry: Omit<ChatOutboxEntry, "retries" | "status" | "createdAt"> & { createdAt?: number },
): Promise<ChatOutboxEntry> {
  const row: ChatOutboxEntry = {
    ...entry,
    createdAt: entry.createdAt ?? Date.now(),
    retries: 0,
    status: "pending",
  };
  transientChatOutbox.set(row.id, row);
  return row;
}

export async function listPendingChatOutbox(): Promise<ChatOutboxEntry[]> {
  const rows = [...transientChatOutbox.values()];
  const pending = rows.filter((row) => row.status === "pending").sort((a, b) => a.createdAt - b.createdAt);
  const held = rows.filter((row) => row.status === "held").sort((a, b) => a.createdAt - b.createdAt);
  return [...pending, ...held];
}

export async function listFailedChatOutbox(): Promise<ChatOutboxEntry[]> {
  return [...transientChatOutbox.values()]
    .filter((row) => row.status === "failed")
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeChatOutbox(id: string): Promise<void> {
  transientChatOutbox.delete(id);
}

export async function markChatOutboxHeld(id: string, holdReason: string): Promise<void> {
  const row = transientChatOutbox.get(id);
  if (!row) {
    return;
  }

  transientChatOutbox.set(id, { ...row, status: "held", holdReason });
}

export async function markChatOutboxPending(id: string): Promise<void> {
  const row = transientChatOutbox.get(id);
  if (!row) {
    return;
  }

  transientChatOutbox.set(id, { ...row, status: "pending", holdReason: undefined });
}

export async function markChatOutboxFailed(id: string, errorMessage: string): Promise<void> {
  const row = transientChatOutbox.get(id);
  if (!row) {
    return;
  }

  transientChatOutbox.set(id, {
    ...row,
    status: "failed",
    errorMessage,
    retries: row.retries + 1,
  });
}

export async function incrementChatOutboxRetry(id: string): Promise<void> {
  const row = transientChatOutbox.get(id);
  if (!row) {
    return;
  }

  transientChatOutbox.set(id, { ...row, retries: row.retries + 1 });
}

export async function countPendingChatOutbox(): Promise<number> {
  const rows = [...transientChatOutbox.values()];
  const pending = rows.filter((row) => row.status === "pending").length;
  const held = rows.filter((row) => row.status === "held").length;
  return pending + held;
}

export async function getChatOutboxItem(id: string): Promise<ChatOutboxEntry | undefined> {
  return transientChatOutbox.get(id);
}

export async function restoreChatOutbox(item: ChatOutboxEntry): Promise<void> {
  transientChatOutbox.set(item.id, item);
}

export function resetTransientChatOutbox(): void {
  transientChatOutbox.clear();
}

export function pendingChatMessageId(outboxId: string): string {
  return `pending-${outboxId}`;
}

export function isPendingChatMessageId(messageId: string): boolean {
  return messageId.startsWith("pending-");
}

export function chatOutboxTargetsConversation(item: ChatOutboxEntry, conversationId: string): boolean {
  if (item.conversationId === conversationId) {
    return true;
  }

  if (item.kind === "sendMessage" && item.args[0] === conversationId) {
    return true;
  }

  return false;
}

export function chatOutboxKindLabel(kind: ChatOutboxKind): string {
  if (kind === "sendMessage") {
    return "mensagem";
  }

  if (kind === "markRead") {
    return "leitura";
  }

  return "conversa";
}
